<script setup lang="ts">
  import dayjs from 'dayjs';
  import { storeToRefs } from 'pinia';
  import pluralize from 'pluralize';
  import { computed, onBeforeMount, ref } from 'vue';

  import type { Invoice, Task } from '@stuut/api/models';
  import { Checkbox } from '@ui/components/checkbox';
  import { Progress } from '@ui/components/progress';
  import { toast } from '@ui/components/sonner';

  import { stuut } from '@/api';
  import InvoiceDrawer from '@/components/common/InvoiceDrawer.vue';
  import PaymentPromiseSubtask from '@/components/tasks/PaymentPromiseSubtask.vue';
  import { useTaskCollectionStore } from '@/store';

  interface Props {
    task?: Task;
  }

  const props = defineProps<Props>();

  const taskCollectionStore = useTaskCollectionStore();
  const { currentTask } = storeToRefs(taskCollectionStore);
  const selectedSubtaskIds = ref<string[]>([]);
  const isDateAreaHovered = ref(false);
  const isBulkUpdating = ref(false);
  const isUpdating = ref(false);

  const selectedSubtasks = computed(() =>
    selectedSubtaskIds.value
      ?.map((id) => subtasks.value.find((tsk) => tsk.id === id))
      .filter((tsk): tsk is Task => !!tsk),
  );

  const completeSubtaskIfReady = async (subtask?: Task) => {
    if (subtask?.isOpen && subtask?.invoice?.promised_to_pay_date) {
      await stuut.tasks.handleCompleteAction(subtask);
      updateTaskGroupStatus(subtask);
    }
  };

  const setPaymentPromiseDate = (invoice: Invoice, promiseDate?: string) => {
    if (!invoice?.id) return;

    return stuut.invoices.update(
      invoice?.id,
      {
        data: {
          promised_to_pay_date: promiseDate || null,
        },
      },
      invoice,
    );
  };

  const updateSubtask = async (subtask?: Task) => {
    if (subtask?.id && selectedSubtaskIds.value?.includes(subtask.id)) {
      if (isBulkUpdating.value) return;

      isBulkUpdating.value = true;

      try {
        const updatePromises = selectedSubtasks.value?.map((currSubtask) =>
          setPaymentPromiseDate(
            currSubtask?.invoice,
            dayjs(subtask?.invoice?.promised_to_pay_date).format('YYYY-MM-DD'),
          ),
        );

        const results = await Promise.allSettled(updatePromises);
        const failures = results.filter((r) => r.status === 'rejected');
        if (failures.length) {
          toast.error(`Failed to update ${failures.length} invoice(s)`);
        }

        const completePromises = selectedSubtasks.value?.map((currSubtask) =>
          completeSubtaskIfReady(currSubtask),
        );

        const completeResults = await Promise.allSettled(completePromises);
        const completeFailures = completeResults.filter((r) => r.status === 'rejected');

        if (completeFailures.length) {
          toast.error(`Failed to complete ${completeFailures.length} task(s)`);
        }

        await updateTaskGroupStatus(subtask);
        await new Promise((resolve) => setTimeout(resolve, 300));
        selectedSubtaskIds.value = [];
        return;
      } finally {
        isBulkUpdating.value = false;
      }
    }

    if (subtask?.isOpen && subtask?.invoice?.promised_to_pay_date) {
      if (isUpdating.value) return;
      isUpdating.value = true;

      try {
        await stuut.tasks.handleCompleteAction(subtask);
        return updateTaskGroupStatus(subtask);
      } catch (err: any) {
        toast.error(err.clientMessage);
      } finally {
        isUpdating.value = false;
      }
    }
  };

  const updateTaskGroupStatus = async (subtask?: Task) => {
    if (!subtask?.group_id) return;

    const taskGroup = await stuut.tasks.retrieve(subtask?.group_id);

    if (taskGroup?.isCompleted || taskGroup?.isClosed) {
      taskGroup.subtasks = [...(currentTask.value?.subtasks ?? [])];
      taskCollectionStore.setCurrentTask(taskGroup);
      taskCollectionStore.afterCompleteTask();
      taskCollectionStore.refreshAllTaskTables();
    }
  };

  const componentTask = computed(() => props.task ?? currentTask.value);
  const subtasks = ref<Task[]>([]);
  const fetchingInvoiceId = ref<string>();

  const selectedInvoice = ref<Invoice | null>(null);
  const showInvoiceSheet = ref(false);

  const isAllSelected = computed(
    () => subtasks.value.length > 0 && selectedSubtaskIds.value.length === subtasks.value.length,
  );

  const isIndeterminate = computed(
    () => selectedSubtaskIds.value.length > 0 && !isAllSelected.value,
  );

  const headerCheckboxValue = computed(() => {
    if (isAllSelected.value) return true;
    if (isIndeterminate.value) return 'indeterminate' as const;
    return false;
  });

  const isAllArchived = computed(() =>
    componentTask.value?.subtasks?.every?.((tsk) => tsk.isArchived),
  );

  const toggleSelectAll = (checked: boolean | 'indeterminate') => {
    selectedSubtaskIds.value =
      checked === true ? subtasks.value.map((t) => t.id).filter((id): id is string => !!id) : [];
  };

  const toggleSubtask = (subtaskId: string | undefined, checked: boolean | 'indeterminate') => {
    if (!subtaskId) return;
    if (checked === true) {
      if (!selectedSubtaskIds.value.includes(subtaskId)) {
        selectedSubtaskIds.value.push(subtaskId);
      }
    } else {
      selectedSubtaskIds.value = selectedSubtaskIds.value.filter((id) => id !== subtaskId);
    }
  };

  const peekFindInvoice = async (invoiceId: string) => {
    const record = props.task?.invoices?.find((inv) => inv.id === invoiceId);

    if (record) return record;

    try {
      fetchingInvoiceId.value = invoiceId;

      const invoice = await stuut.invoices.retrieve(invoiceId);
      return invoice || undefined;
    } finally {
      fetchingInvoiceId.value = undefined;
    }
  };

  const handleSheetClose = () => {
    selectedInvoice.value = null;
    showInvoiceSheet.value = false;
  };

  const handleSheetOpen = async (invoiceId?: string) => {
    if (!invoiceId) return;

    selectedInvoice.value = await peekFindInvoice(invoiceId);

    showInvoiceSheet.value = true;
  };

  onBeforeMount(async () => {
    const subtaskPromises: Promise<Task>[] = [];

    componentTask.value?.activeSubtasks?.forEach((subtask: Task) => {
      if (subtask?.id) {
        subtaskPromises.push(stuut.tasks.retrieve(subtask?.id));
      }
    });

    subtasks.value = await Promise.all(subtaskPromises);
  });

  const completedActiveSubtasks = computed(() =>
    subtasks.value?.filter((task) => task?.isCompleted || task?.isClosed),
  );

  const canCompleteAllSubtasks = computed(() =>
    subtasks.value?.every((task) => Boolean(task?.invoice?.promised_to_pay_date)),
  );

  const progress = computed(
    () => ((completedActiveSubtasks.value?.length || 0) / (subtasks.value?.length || 1)) * 100,
  );

  defineExpose({
    canCompleteAllSubtasks,
  });
</script>

<template>
  <div class="space-y-4">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-x-1.5">
        <i class="far fa-calendar-day text-2xs!"></i>

        <strong>
          {{ pluralize('Promised Date', componentTask?.promisesData?.length) }}

          <template v-if="componentTask?.isExpectsAcknowledge"> set by agent: </template>
          <template v-else> with issues:</template>
        </strong>
      </div>

      <div v-if="subtasks?.length" class="flex gap-x-2.5 items-center">
        <span class="text-2xs">
          {{ completedActiveSubtasks?.length || 0 }} of {{ subtasks?.length }}
          {{ pluralize('task', completedActiveSubtasks?.length) }}
        </span>

        <Progress :model-value="progress" completed-class="bg-success" class="h-1.5 w-20" />
      </div>
    </div>

    <div class="w-full border rounded">
      <div
        class="grid grid-cols-[36px_50px_minmax(135px,auto)_82px_70px_125px_70px_46px_82px_28px_28px_1fr] items-center gap-x-1 w-full h-7 text-muted-foreground font-semibold"
      >
        <div class="flex justify-center pl-2">
          <Checkbox :model-value="headerCheckboxValue" @update:model-value="toggleSelectAll" />
        </div>
        <div class="pr-2">&nbsp;</div>
        <div>Invoice</div>
        <div>Due date</div>
        <div>Balance</div>
        <div>Promised date</div>
        <div>Amount</div>
        <div>Type</div>
        <div>Status</div>
      </div>

      <template v-for="subtask in subtasks" :key="subtask?.id">
        <PaymentPromiseSubtask
          :task="subtask"
          :is-selected="!!subtask?.id && selectedSubtaskIds.includes(subtask?.id)"
          :is-date-hovered="isDateAreaHovered"
          @hover-date="(val) => (isDateAreaHovered = val)"
          @toggle-select="(checked) => toggleSubtask(subtask?.id, checked)"
          @after-set-date="updateSubtask"
          @after-complete="updateTaskGroupStatus"
          @open-sheet="handleSheetOpen"
          @close-sheet="handleSheetClose"
        />
      </template>

      <div v-if="isAllArchived" class="w-full text-center pt-6 pb-4 text-muted-foreground">
        All subtasks archived
      </div>
    </div>

    <InvoiceDrawer
      v-if="selectedInvoice"
      v-model:open="showInvoiceSheet"
      :invoice="selectedInvoice"
      :partner="selectedInvoice.partner"
      @close="handleSheetClose"
    />
  </div>
</template>

<style scoped></style>
